export interface GamepadButtonLike {
  pressed: boolean;
  value: number;
}

export interface GamepadLike {
  id: string;
  index: number;
  axes: readonly number[];
  buttons: readonly GamepadButtonLike[];
  connected?: boolean;
}

export interface GamepadSample {
  connected: boolean;
  deviceId?: string;
  deviceHash?: string;
  axes: number[];
  buttons: number[];
}

export type GetGamepads = () => Array<GamepadLike | null>;

/** Stable FNV-1a hash rendered as eight lowercase hexadecimal digits. */
export const hashGamepadId = (id: string): string => {
  let hash = 0x811c9dc5;
  for (let index = 0; index < id.length; index += 1) {
    hash ^= id.charCodeAt(index);
    hash = Math.imul(hash, 0x01000193);
  }
  return (hash >>> 0).toString(16).padStart(8, '0');
};

/** Poll-based Gamepad API adapter; browser access is supplied by the caller. */
export class GamepadSource {
  onConnect: (deviceId: string, deviceHash: string) => void = () => undefined;
  onDisconnect: () => void = () => undefined;

  private activeKey: string | null = null;

  constructor(private readonly getGamepads: GetGamepads) {}

  poll(): GamepadSample {
    const pad = this.getGamepads().find((candidate) => candidate?.connected !== false) ?? null;
    if (pad === null) {
      if (this.activeKey !== null) {
        this.activeKey = null;
        this.onDisconnect();
      }
      return { connected: false, axes: [], buttons: [] };
    }

    const key = `${pad.index}:${pad.id}`;
    const deviceHash = hashGamepadId(pad.id);
    if (key !== this.activeKey) {
      if (this.activeKey !== null) this.onDisconnect();
      this.activeKey = key;
      this.onConnect(pad.id, deviceHash);
    }

    return {
      connected: true,
      deviceId: pad.id,
      deviceHash,
      axes: Array.from(pad.axes),
      buttons: Array.from(pad.buttons, (button) =>
        button.pressed ? Math.max(1, button.value) : button.value,
      ),
    };
  }
}
